import { Autocomplete, Box, Chip, TextField, Typography, useTheme, useMediaQuery } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Getalldata_Leads_depending_on_Sales_Action } from '../Redux/Actions/UsersAction';
import { Get_All_Sales_By_Managers_Email_Action } from '../Redux/Actions/SalesAction';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Link } from 'react-router-dom';
import { useSidebarWidth } from '../Utility/useSidebarWidth';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* ─── Design tokens — matches ManagerDashboard / MyTeamLeadersForManagers ── */
const C = {
  bg: '#F7F8FC',
  white: '#FFFFFF',
  border: '#E8EAF0',
  accent: '#0D47A1',
  accentSoft: 'rgba(13,71,161,0.07)',
  text: '#1C2340',
  textMuted: '#7B829A',
  success: '#12B76A',
  warning: '#F79009',
  danger: '#F04438',
  shadow: '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd: '0 4px 16px rgba(16,24,40,0.10)',
};

const FONT = "'DM Sans', sans-serif";
const SIDEBAR_COLLAPSED = 64;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: C.white,
    fontFamily: FONT,
    fontSize: '13.5px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.accent },
    '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: '13.5px', color: C.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: C.accent },
};

export const ManagerTeamAnylytics = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sidebarW = useSidebarWidth();

  const [selectedSale, setSelectedSale] = useState(null);
  const dispatch = useDispatch();
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [totalLeads, setTotalLeads] = useState(0);

  // Selectors
  const Allsales = useSelector((state) => state.AllSales.salesforManagers?.data || []);
  const leadsData = useSelector((state) => state.Allusers.Get_users_Depending_On_sales || []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.data?.email) {
      dispatch(Get_All_Sales_By_Managers_Email_Action(user?.data?.email));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedSale?._id) {
      dispatch(Getalldata_Leads_depending_on_Sales_Action(selectedSale?._id));
    }
  }, [selectedSale, dispatch]);

  const filterLeadsByDateRange = (leads) => {
    if (!dateFrom && !dateTo) return leads;

    return leads.filter((lead) => {
      const leadDate = lead.date?.split('T')[0];
      if (!leadDate) return false;

      if (dateFrom && dateTo) {
        return leadDate >= dateFrom && leadDate <= dateTo;
      }
      if (dateFrom) return leadDate >= dateFrom;
      if (dateTo) return leadDate <= dateTo;
      return true;
    });
  };

  const prepareChartData = () => {
    if (!Array.isArray(leadsData) || leadsData.length === 0) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            label: 'Number of Leads',
            data: [0],
            backgroundColor: C.accent,
            borderWidth: 1,
          },
        ],
      };
    }

    let total = 0;
    const processedStages = leadsData.map((stage) => {
      const filteredLeads = filterLeadsByDateRange(stage.leads || []);
      total += filteredLeads.length;
      return {
        stageName: stage.stageName,
        count: filteredLeads.length,
        leads: filteredLeads,
      };
    });

    setTotalLeads(total); // Update total leads count

    const labels = processedStages.map((stage) => stage.stageName);
    const data = processedStages.map((stage) => stage.count);

    return {
      labels,
      datasets: [
        {
          label: 'Number of Leads',
          data,
          backgroundColor: C.accent,
          borderColor: C.border,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  const handleGenerateReport = () => {
    if (!selectedSale) {
      alert('Please select a sales person first');
      return;
    }

    setIsLoading(true);
    try {
      const data = prepareChartData();
      setChartData(data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `Leads Distribution for ${selectedSale?.name || ''}`,
        font: { size: 14, family: FONT, weight: '700' },
        color: C.text,
      },
      tooltip: {
        backgroundColor: C.white,
        titleColor: C.text,
        bodyColor: C.textMuted,
        borderColor: C.border,
        borderWidth: 1,
        titleFont: { family: FONT },
        bodyFont: { family: FONT },
        callbacks: {
          label: (context) =>
            `${context.parsed.x} leads (${Math.round((context.parsed.x / totalLeads) * 100)}%)`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: C.border },
        ticks: { font: { family: FONT }, color: C.textMuted },
        title: {
          display: true,
          text: 'Number of Leads',
          font: { weight: 'bold', family: FONT },
          color: C.textMuted,
        },
      },
      y: {
        grid: { display: false },
        ticks: { font: { family: FONT }, color: C.text },
        title: {
          display: true,
          text: 'Sales Stage',
          font: { weight: 'bold', family: FONT },
          color: C.textMuted,
        },
      },
    },
  };

  return (
    <div
      style={{
        marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
        marginTop: 64,
        minHeight: 'calc(100vh - 64px)',
        background: C.bg,
        fontFamily: FONT,
        transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      <Container fluid className="fade-in" style={{ padding: '28px 24px 48px', maxWidth: '100%' }}>
        {/* ── Breadcrumb ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <Link to="/LeadsForManagers" style={{ textDecoration: 'none' }}>
            <span style={{ fontWeight: 700, color: C.accent, fontFamily: FONT, fontSize: 13 }}>
              Home
            </span>
          </Link>
          <KeyboardDoubleArrowRightIcon sx={{ fontSize: 16, color: C.textMuted }} />
          <span style={{ color: C.textMuted, fontFamily: FONT, fontSize: 13 }}>
            Sales Leads Analytics
          </span>
        </div>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <StackedLineChartOutlinedIcon sx={{ color: C.accent, fontSize: 22 }} />
          <span
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.text,
              letterSpacing: '-0.02em',
              fontFamily: FONT,
            }}
          >
            Sales Leads Analytics
          </span>
        </div>

        {/* ── Filter panel ── */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: C.shadow,
            padding: '22px 24px',
            marginBottom: 24,
            maxWidth: 760,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: FONT, marginBottom: 4 }}>
            Choose Sales Then Choose Dates From And To
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginBottom: 18 }}>
            Pick a sales person and an optional date range to build the report
          </div>

          <div className="d-flex flex-column gap-3">
            <Autocomplete
              options={Allsales}
              onChange={(e, newValue) => setSelectedSale(newValue)}
              getOptionLabel={(option) => option.name || 'Unknown'}
              sx={fieldSx}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    fontFamily: FONT,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: C.text }}>{option.name}</div>
                    <div style={{ fontSize: '0.78rem', color: C.textMuted }}>
                      {option?.userlog?.email || 'No email'}
                    </div>
                  </div>
                  <Chip
                    label={option?.userlog?.role || 'No role'}
                    size="small"
                    sx={{
                      fontFamily: FONT,
                      fontSize: 11,
                      fontWeight: 600,
                      background: C.accentSoft,
                      color: C.accent,
                    }}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Select Sales Person" variant="outlined" fullWidth />
              )}
            />

            <div className="d-flex gap-3">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={dateFrom ? dayjs(dateFrom) : null}
                  onChange={(newValue) => setDateFrom(newValue?.format('YYYY-MM-DD'))}
                  sx={{ flex: 1, ...fieldSx }}
                />
                <DatePicker
                  label="To Date"
                  value={dateTo ? dayjs(dateTo) : null}
                  onChange={(newValue) => setDateTo(newValue?.format('YYYY-MM-DD'))}
                  sx={{ flex: 1, ...fieldSx }}
                  minDate={dateFrom ? dayjs(dateFrom) : null}
                />
              </LocalizationProvider>
            </div>

            <div className="d-flex justify-content-center w-100" style={{ marginTop: 6 }}>
              <button
                onClick={handleGenerateReport}
                disabled={isLoading}
                style={{
                  width: '100%',
                  maxWidth: 420,
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 20px',
                  background: C.accent,
                  color: '#fff',
                  fontFamily: FONT,
                  fontSize: 13.5,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  boxShadow: '0 4px 14px rgba(13,71,161,0.25)',
                  transition: 'opacity 0.15s, transform 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <FilterAltIcon sx={{ fontSize: 18 }} />
                {isLoading ? 'Generating Report...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Summary ── */}
        {selectedSale && (
          <div
            style={{
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              boxShadow: C.shadow,
              padding: '20px 24px',
              marginBottom: 20,
              maxWidth: 760,
            }}
          >
            <div style={{ fontSize: 14.5, fontWeight: 700, color: C.accent, fontFamily: FONT, marginBottom: 6 }}>
              Sales Summary for: <span style={{ color: C.text }}>{selectedSale.name}</span>
            </div>
            <div style={{ fontSize: 13.5, color: C.text, fontFamily: FONT }}>
              Total Leads: <strong>{totalLeads}</strong>
            </div>
            {dateFrom && dateTo && (
              <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginTop: 4 }}>
                Filtered by date: {dayjs(dateFrom).format('DD/MM/YYYY')} to{' '}
                {dayjs(dateTo).format('DD/MM/YYYY')}
              </div>
            )}
          </div>
        )}

        {/* ── Chart ── */}
        {chartData && (
          <div
            style={{
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              boxShadow: C.shadow,
              padding: '24px',
              width: '100%',
            }}
          >
            <Chart type="bar" data={chartData} options={chartOptions} />
          </div>
        )}
      </Container>
    </div>
  );
};
